/**
 * Central date formatting utility that respects user preferences
 *
 * Usage:
 *   import { useDateFormatter } from '../hooks/useDateFormatter';
 *   const { formatDate, formatDateTime, formatTime, formatRelative } = useDateFormatter();
 *
 *   // In component:
 *   <span>{formatDateTime(ticket.createdAt)}</span>
 */

import { getISOWeek } from 'date-fns';

export interface DateFormatPreferences {
  timezone: string;
  dateFormat: 'MM/dd/yyyy' | 'dd/MM/yyyy' | 'yyyy-MM-dd' | 'dd-MMM-yyyy';
  timeFormat: '12H' | '24H';
}

export const DEFAULT_PREFERENCES: DateFormatPreferences = {
  timezone: 'Asia/Kolkata',
  dateFormat: 'MM/dd/yyyy',
  timeFormat: '24H',
};

function formatPartsAsUtcMs(timeZone: string, instant: Date): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = dtf.formatToParts(instant);
  const get = (type: string): string => parts.find((p) => p.type === type)?.value || '0';
  let hour = Number(get('hour'));
  if (hour === 24) hour = 0;
  return Date.UTC(
    Number(get('year')),
    Number(get('month')) - 1,
    Number(get('day')),
    hour,
    Number(get('minute')),
    Number(get('second'))
  );
}

/** Milliseconds to add to UTC to obtain wall clock in `timeZone`. */
function timeZoneOffsetMs(timeZone: string, instant: Date): number {
  return formatPartsAsUtcMs(timeZone, instant) - instant.getTime();
}

export function zonedWallClockToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  ms: number,
  timeZone: string
): Date {
  const asUtcWhole = Date.UTC(year, month - 1, day, hour, minute, second, 0);
  const offset = timeZoneOffsetMs(timeZone, new Date(asUtcWhole));
  let result = asUtcWhole - offset;
  const offset2 = timeZoneOffsetMs(timeZone, new Date(result));
  if (offset2 !== offset) {
    result = asUtcWhole - offset2;
  }
  return new Date(result + ms);
}

function hasExplicitTimezone(s: string): boolean {
  if (s.endsWith('Z') || s.endsWith('z')) return true;
  if (s.length < 6) return false;
  const sign = s.charAt(s.length - 6);
  return (sign === '+' || sign === '-') && s.charAt(s.length - 3) === ':';
}

/**
 * Interpret a datetime-local / YYYY-MM-DD KPI Duration bound as wall clock in
 * `timeZone` (same zone as formatDateTime) and return an ISO UTC instant.
 */
export function dateTimeLocalInTimeZoneToIso(
  raw: string,
  timeZone: string,
  endOfDayBound: boolean
): string {
  const s = (raw || '').trim();
  if (!s) return '';
  if (hasExplicitTimezone(s)) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString();
  }

  let normalized = s;
  if (!normalized.includes('T')) {
    normalized = endOfDayBound ? `${normalized}T23:59:59.999` : `${normalized}T00:00:00.000`;
  } else {
    const timePart = normalized.split('T')[1] || '';
    const timeParts = timePart.split(':');
    if (timeParts.length === 2) {
      normalized = endOfDayBound ? `${normalized}:59.999` : `${normalized}:00.000`;
    } else if (timeParts.length === 3) {
      const secondsPart = timeParts[2];
      if (secondsPart && !secondsPart.includes('.')) {
        normalized = `${normalized}.000`;
      }
    }
  }

  const tIdx = normalized.indexOf('T');
  if (tIdx < 8) return '';
  const datePart = normalized.slice(0, tIdx);
  const timePart = normalized.slice(tIdx + 1);
  const year = Number(datePart.slice(0, 4));
  const month = Number(datePart.slice(5, 7));
  const day = Number(datePart.slice(8, 10));
  const hour = Number(timePart.slice(0, 2));
  const minute = Number(timePart.slice(3, 5));
  const second = Number(timePart.slice(6, 8));
  let ms = 0;
  const dot = timePart.indexOf('.');
  if (dot >= 0) {
    ms = Number(timePart.slice(dot + 1, dot + 4).padEnd(3, '0'));
  }
  if (![year, month, day, hour, minute, second, ms].every((n) => Number.isFinite(n))) {
    return '';
  }
  return zonedWallClockToUtc(year, month, day, hour, minute, second, ms, timeZone).toISOString();
}

/** JS getTimezoneOffset() minutes for an IANA zone (IST = -330). */
export function getTimezoneOffsetMinutesForZone(
  timeZone: string,
  at: Date = new Date()
): number {
  return -Math.round(timeZoneOffsetMs(timeZone, at) / 60000);
}

/**
 * Format a date/time string according to user preferences
 */
export function formatDateTimeWithPreferences(
  dateInput: string | Date | null | undefined,
  preferences: DateFormatPreferences,
  options: { includeTime?: boolean; includeSeconds?: boolean } = {}
): string {
  if (!dateInput) return '-';

  const { includeTime = true, includeSeconds = false } = options;
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

  if (isNaN(date.getTime())) return '-';

  try {
    // Format the date part
    const datePart = formatDatePart(date, preferences);

    if (!includeTime) {
      return datePart;
    }

    // Format the time part
    const timePart = formatTimePart(date, preferences, includeSeconds);

    return `${datePart} ${timePart}`;
  } catch (error) {
    console.error('Date formatting error:', error);
    return date.toISOString();
  }
}

/**
 * Format just the date part according to preferences
 */
function formatDatePart(date: Date, preferences: DateFormatPreferences): string {
  const options: Intl.DateTimeFormatOptions = {
    timeZone: preferences.timezone,
  };

  // Get formatted parts
  const formatter = new Intl.DateTimeFormat('en-US', {
    ...options,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const parts = formatter.formatToParts(date);
  const month = parts.find(p => p.type === 'month')?.value || '';
  const day = parts.find(p => p.type === 'day')?.value || '';
  const year = parts.find(p => p.type === 'year')?.value || '';

  // Format according to user preference
  switch (preferences.dateFormat) {
    case 'dd/MM/yyyy':
      return `${day}/${month}/${year}`;
    case 'yyyy-MM-dd':
      return `${year}-${month}-${day}`;
    case 'dd-MMM-yyyy': {
      const monthFormatter = new Intl.DateTimeFormat('en-US', {
        ...options,
        month: 'short',
      });
      const monthShort = monthFormatter.format(date);
      return `${day}-${monthShort}-${year}`;
    }
    case 'MM/dd/yyyy':
    default:
      return `${month}/${day}/${year}`;
  }
}

/**
 * Format just the time part according to preferences
 */
function formatTimePart(
  date: Date,
  preferences: DateFormatPreferences,
  includeSeconds: boolean = false
): string {
  const options: Intl.DateTimeFormatOptions = {
    timeZone: preferences.timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: preferences.timeFormat === '12H',
  };

  if (includeSeconds) {
    options.second = '2-digit';
  }

  return date.toLocaleTimeString('en-US', options);
}

/**
 * Format date only (no time)
 */
export function formatDateWithPreferences(
  dateInput: string | Date | null | undefined,
  preferences: DateFormatPreferences
): string {
  return formatDateTimeWithPreferences(dateInput, preferences, { includeTime: false });
}

/**
 * Format time only
 */
export function formatTimeWithPreferences(
  dateInput: string | Date | null | undefined,
  preferences: DateFormatPreferences,
  includeSeconds: boolean = false
): string {
  if (!dateInput) return '-';

  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

  if (isNaN(date.getTime())) return '-';

  return formatTimePart(date, preferences, includeSeconds);
}

/**
 * Format relative time (e.g., "2 hours ago", "in 3 days")
 */
export function formatRelativeTime(
  dateInput: string | Date | null | undefined,
  preferences: DateFormatPreferences
): string {
  if (!dateInput) return '-';

  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

  if (isNaN(date.getTime())) return '-';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(Math.abs(diffMs) / 60000);
  const diffHours = Math.floor(Math.abs(diffMs) / 3600000);
  const diffDays = Math.floor(Math.abs(diffMs) / 86400000);

  const isFuture = diffMs < 0;

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) {
    return isFuture ? `in ${diffMins}m` : `${diffMins}m ago`;
  }
  if (diffHours < 24) {
    return isFuture ? `in ${diffHours}h` : `${diffHours}h ago`;
  }
  if (diffDays < 7) {
    return isFuture ? `in ${diffDays}d` : `${diffDays}d ago`;
  }

  // Fallback to formatted date for older/future dates
  return formatDateWithPreferences(date, preferences);
}

/**
 * Format duration in minutes to human-readable format
 */
export function formatDuration(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || minutes < 0) return '-';

  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours < 24) {
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;

  return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
}

/**
 * Format duration in seconds to human-readable format
 */
export function formatDurationSeconds(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || seconds < 0) return '-';

  if (seconds < 60) return `${seconds}s`;

  return formatDuration(Math.floor(seconds / 60));
}

/**
 * Convert a date to the user's timezone for display in datetime-local inputs
 * This is useful for pre-filling form fields with dates from the database
 */
export function formatForDateTimeInput(
  dateInput: string | Date | null | undefined,
  timezone: string = 'Asia/Kolkata'
): string {
  if (!dateInput) return '';

  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

  if (isNaN(date.getTime())) return '';

  // Format in the target timezone
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const parts = formatter.formatToParts(date);
  const year = parts.find(p => p.type === 'year')?.value || '';
  const month = parts.find(p => p.type === 'month')?.value || '';
  const day = parts.find(p => p.type === 'day')?.value || '';
  const hour = parts.find(p => p.type === 'hour')?.value || '';
  const minute = parts.find(p => p.type === 'minute')?.value || '';

  return `${year}-${month}-${day}T${hour}:${minute}`;
}

/**
 * Convert a date to the user's timezone for display in date inputs
 */
export function formatForDateInput(
  dateInput: string | Date | null | undefined,
  timezone: string = 'Asia/Kolkata'
): string {
  if (!dateInput) return '';

  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

  if (isNaN(date.getTime())) return '';

  // Format in the target timezone
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const parts = formatter.formatToParts(date);
  const year = parts.find(p => p.type === 'year')?.value || '';
  const month = parts.find(p => p.type === 'month')?.value || '';
  const day = parts.find(p => p.type === 'day')?.value || '';

  return `${year}-${month}-${day}`;
}

/** Day-of-week, ISO week, and month labels in the user's preferred timezone (KPI exports / tables). */
export function formatCalendarPartsInTimezone(
  dateInput: string | Date | null | undefined,
  timezone: string = DEFAULT_PREFERENCES.timezone
): { day: string; week: string; month: string } {
  if (!dateInput) return { day: '—', week: '—', month: '—' };

  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  if (Number.isNaN(date.getTime())) return { day: '—', week: '—', month: '—' };

  const dayFormatter = new Intl.DateTimeFormat('en-US', { timeZone: timezone, weekday: 'short' });
  const monthFormatter = new Intl.DateTimeFormat('en-US', { timeZone: timezone, month: 'short' });
  const dateFormatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const dateParts = dateFormatter.formatToParts(date);
  const y = Number(dateParts.find((p) => p.type === 'year')?.value);
  const m = Number(dateParts.find((p) => p.type === 'month')?.value);
  const d = Number(dateParts.find((p) => p.type === 'day')?.value);
  const w = getISOWeek(new Date(y, m - 1, d));

  return {
    day: dayFormatter.format(date).slice(0, 2),
    week: `Week ${w}`,
    month: monthFormatter.format(date),
  };
}

/** Excel number format matching user date/time preferences (for KPI backlog detail exports). */
export function excelDateTimeNumberFormat(preferences: DateFormatPreferences): string {
  const datePart: Record<DateFormatPreferences['dateFormat'], string> = {
    'MM/dd/yyyy': 'mm/dd/yyyy',
    'dd/MM/yyyy': 'dd/mm/yyyy',
    'yyyy-MM-dd': 'yyyy-mm-dd',
    'dd-MMM-yyyy': 'dd-mmm-yyyy',
  };
  const timePart = preferences.timeFormat === '12H' ? 'hh:mm:ss AM/PM' : 'hh:mm:ss';
  return `${datePart[preferences.dateFormat]} ${timePart}`;
}

/**
 * Convert an ISO timestamp to an Excel serial datetime using wall-clock time in the user's timezone.
 * Returns '' for null/empty/invalid values.
 */
export function isoToExcelSerialInTimezone(
  iso: string | null | undefined,
  timezone: string = DEFAULT_PREFERENCES.timezone
): number | '' {
  if (!iso || !String(iso).trim()) return '';

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(date);
  const year = Number(parts.find((p) => p.type === 'year')?.value);
  const month = Number(parts.find((p) => p.type === 'month')?.value);
  const day = Number(parts.find((p) => p.type === 'day')?.value);
  const hour = Number(parts.find((p) => p.type === 'hour')?.value);
  const minute = Number(parts.find((p) => p.type === 'minute')?.value);
  const second = Number(parts.find((p) => p.type === 'second')?.value ?? 0);

  const utcMs = Date.UTC(year, month - 1, day, hour, minute, second);
  return utcMs / 86400000 + 25569;
}

/**
 * Excel serial from a true UTC instant (for cells referenced by duration formulas).
 * Returns '' for null/empty/invalid values.
 */
export function isoToExcelSerialUtc(iso: string | null | undefined): number | '' {
  if (!iso || !String(iso).trim()) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.getTime() / 86400000 + 25569;
}

/**
 * Format a date-only value (e.g. workedDate stored as UTC midnight) using the
 * UTC calendar date so it displays the same everywhere (list, edit, modals).
 * Does not apply timezone conversion.
 */
export function formatDateOnlyWithPreferences(
  dateInput: string | Date | null | undefined,
  preferences: DateFormatPreferences
): string {
  if (!dateInput) return '-';

  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  if (isNaN(date.getTime())) return '-';

  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');

  switch (preferences.dateFormat) {
    case 'dd/MM/yyyy':
      return `${day}/${month}/${year}`;
    case 'yyyy-MM-dd':
      return `${year}-${month}-${day}`;
    case 'dd-MMM-yyyy': {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthShort = monthNames[date.getUTCMonth()];
      return `${day}-${monthShort}-${year}`;
    }
    case 'MM/dd/yyyy':
    default:
      return `${month}/${day}/${year}`;
  }
}
